import { UserIdSchema } from "@core/domains/identity";
import { identity } from "@core/foundation/function";
import { ulid } from "@core/foundation/id";
import {
  createModel,
  createModelId,
  createModelSchemas,
  noModelUpdate,
} from "@core/foundation/model";
import { z } from "@core/zod";

import { FolderStructureSchema } from "./folder-structure.ts";
import { ID_PREFIX } from "./shared/id-prefix.ts";
import { TaxonomyIdSchema } from "./shared/taxonomy-id.ts";

const {
  generateId: generateFolderStructureRevisionId,
  idSchema: FolderStructureRevisionIdSchema,
} = createModelId({
  digestFn: ulid,
  idPrefix: ID_PREFIX.FOLDER_STRUCTURE_REVISION,
});

export { FolderStructureRevisionIdSchema };

export type FolderStructureRevisionId = z.infer<
  typeof FolderStructureRevisionIdSchema
>;

export const FolderStructureRevision = createModel({
  create: (item, schema) => {
    return { ...schema.decode(item), id: generateFolderStructureRevisionId() };
  },
  schemas: createModelSchemas({
    base: z.object(
      {
        folderStructure: FolderStructureSchema,
        id: FolderStructureRevisionIdSchema,
        principalId: UserIdSchema,
        taxonomyId: TaxonomyIdSchema,
      },
      {
        folderStructure: "The taxonomy's folder structure as of this revision.",
        id: "The revision's id, ordered by the time the revision was saved.",
        principalId: "The id of the user the revision belongs to.",
        taxonomyId: "The id of the taxonomy the revision belongs to.",
      },
    ),
    derive: {
      create: schema => schema.omit({ id: true }),
      recovery: identity,
      update: noModelUpdate,
    },
  }),
  update: (item, update, schema) => {
    return { ...item, ...schema.decode(update) };
  },
});

export interface FolderStructureRevision
  extends z.infer<typeof FolderStructureRevision.schema> {}
